import React from 'react';
import { Link } from 'react-router-dom';
import UICard from 'components/ui/UICard';
import UIFlex from 'components/ui/UIFlex';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useAppSelector } from 'app/hooks';
import commonSlice from 'app/store/common.slice';
import { WordSetCategoryType } from 'app/types';

/**
 * Compact catalog row used across /wordset. Replaces the seven full-card
 * sections (PhrasalVerbs, Collocations, Idioms, MostCommonWords, IrregularVerbs,
 * ThematicLexicon) with a single reusable component. Stat numbers come from the
 * same Redux selector as before.
 *
 * Casing/naming note: row's `id` doubles as the Redux key AND the `wordSetType`
 * URL parameter — the existing routes are mapped 1:1 (e.g. `phrasal-verbs`).
 */
export interface IWordSetRowProps {
  id: WordSetCategoryType;
  title: string;
  en: string;
  desc: string;
  illustration: string;
  /** Accent hex — used as 10%-tint thumbnail bg, eyebrow color and hover border. */
  accent: string;
  /** Stat-pill units; defaults match most categories (тем · уроків · слів). */
  units?: { category: string; sub: string; words: string };
}

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const StatPill: React.FC<{ n?: number | string; u: string }> = ({ n, u }) => (
  <UIBox
    sx={{
      alignItems: 'baseline',
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 999,
      display: 'inline-flex',
      fontSize: 13,
      gap: 0.6,
      px: 1.4,
      py: 0.6,
      whiteSpace: 'nowrap',
    }}
  >
    <UIText component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
      {n ?? 0}
    </UIText>
    <UIText component={'span'} color={'primary.subtitle'} sx={{ fontSize: 13 }}>
      {u}
    </UIText>
  </UIBox>
);

const WordSetRow: React.FC<IWordSetRowProps> = ({
  id,
  title,
  en,
  desc,
  illustration,
  accent,
  units = { category: 'тем', sub: 'уроків', words: 'слів' },
}) => {
  const data = useAppSelector((store) =>
    commonSlice.getSelectors().getWordSetDataById(store.common, id),
  );

  return (
    <Link
      to={fillUrlIds(pagePaths.wordSetCategoryOverview, { wordSetType: id })}
    >
      <UICard
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          p: 0,
          // Intentionally NOT `transition: all` — bg uses theme variables and
          // gets "stuck" mid-transition when the user toggles dark/light.
          // See handoff README → "Гоча темної теми". Hover bg-tint is rgba
          // (static, not a theme variable) so an instant snap is fine.
          transition: 'border-color .16s ease',
          '&:hover': {
            borderColor: accent,
            bgcolor: hexToRgba(accent, 0.04),
          },
          '&:hover .ws-row-arrow': {
            transform: 'translateX(4px)',
          },
        }}
      >
        <UIFlex
          ai={'center'}
          sx={{
            flexWrap: 'nowrap',
            gap: { xs: 1.75, md: 2.5 },
            p: { xs: '14px', md: '16px 20px' },
          }}
        >
          {/* Thumbnail with accent-tinted background */}
          <UIBox
            sx={{
              alignItems: 'center',
              bgcolor: hexToRgba(accent, 0.1),
              borderRadius: 2,
              display: 'flex',
              flex: 'none',
              height: { xs: 60, md: 92 },
              justifyContent: 'center',
              overflow: 'hidden',
              width: { xs: 60, md: 92 },
            }}
          >
            <UIBox
              alt={''}
              component={'img'}
              src={illustration}
              sx={{
                maxHeight: '78%',
                maxWidth: '78%',
                objectFit: 'contain',
              }}
            />
          </UIBox>

          {/* Content */}
          <UIBox sx={{ flex: 1, minWidth: 0 }}>
            <UIText
              sx={{
                color: 'text.primary',
                fontSize: { xs: 10, md: 11 },
                fontWeight: 800,
                letterSpacing: '0.09em',
                mb: 0.5,
                textTransform: 'uppercase',
              }}
            >
              {en}
            </UIText>
            <UIText
              variant={'h3'}
              sx={{ color: accent, fontSize: { xs: 17, md: 21 }, mb: 0.6 }}
            >
              {title}
            </UIText>
            <UIText
              color={'primary.subtitle'}
              sx={{
                display: '-webkit-box',
                fontSize: { xs: 13, md: 14 },
                mb: 1.25,
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: { xs: 2, md: 1 },
              }}
            >
              {desc}
            </UIText>
            <UIFlex jc={'flex-start'} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <StatPill n={data?.categoryCount} u={units.category} />
              <StatPill n={data?.subCategoryCount} u={units.sub} />
              <StatPill n={data?.wordsCount} u={units.words} />
            </UIFlex>
          </UIBox>

          {/* CTA */}
          <UIBox
            sx={{
              alignItems: 'center',
              color: accent,
              display: { xs: 'none', sm: 'inline-flex' },
              flex: 'none',
              fontSize: 15,
              fontWeight: 600,
              gap: 0.5,
              pr: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            {'Відкрити'}
            <UIBox
              className={'ws-row-arrow'}
              component={'span'}
              sx={{
                display: 'inline-block',
                transition: 'transform .25s cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              {'→'}
            </UIBox>
          </UIBox>
        </UIFlex>
      </UICard>
    </Link>
  );
};

export default WordSetRow;
